/**
 * Shared window preset constants and types
 * Used by WindowPresetBar and useViewerHotkeys
 */

export type WindowPreset = "soft_tissue" | "lung" | "bone" | "brain" | "liver";

export interface PresetConfig {
  id: WindowPreset;
  label: string;
  shortLabel: string;
  center: number;
  width: number;
  numberKey: string;
  letterKey: string;
}

export const WINDOW_PRESETS: PresetConfig[] = [
  {
    id: "soft_tissue",
    label: "Soft Tissue",
    shortLabel: "ST",
    center: 40,
    width: 400,
    numberKey: "Shift+1",
    letterKey: "Shift+T",
  },
  {
    id: "lung",
    label: "Lung",
    shortLabel: "Lu",
    center: -600,
    width: 1500,
    numberKey: "Shift+2",
    letterKey: "Shift+L",
  },
  {
    id: "bone",
    label: "Bone",
    shortLabel: "Bo",
    center: 400,
    width: 1800,
    numberKey: "Shift+3",
    letterKey: "Shift+B",
  },
  {
    id: "brain",
    label: "Brain",
    shortLabel: "Br",
    center: 40,
    width: 80,
    numberKey: "Shift+4",
    letterKey: "Shift+R",
  },
  {
    id: "liver",
    label: "Liver",
    shortLabel: "Li",
    center: 60,
    width: 150,
    numberKey: "Shift+5",
    letterKey: "Shift+V",
  },
];

const SOFT_TISSUE_PRESET = WINDOW_PRESETS.find((p) => p.id === "soft_tissue")!;
export const DEFAULT_WINDOWING = {
  center: SOFT_TISSUE_PRESET.center,
  width: SOFT_TISSUE_PRESET.width,
};

/**
 * The window a jump to a finding opens in, chosen by its condition key's
 * anatomy prefix (first matching rule). Anything else opens in soft tissue, so
 * a finding on a lung-kernel series is not shown at that series' own window.
 */
const FINDING_WINDOW_RULES: readonly { prefixes: readonly string[]; preset: WindowPreset }[] = [
  { prefixes: ["lungs/"], preset: "lung" },
  // `bones` covers every bones_* region (bones_pelvis/, bones_skull/, ...).
  { prefixes: ["bones", "spine/", "musculoskeletal/"], preset: "bone" },
];

/** The window for a finding: the first of its condition keys (a grouped finding
 * has one per device) that a rule names, else soft tissue. */
export function findingWindow(conditions: readonly string[]): { center: number; width: number } {
  const id =
    conditions
      .map(
        (condition) =>
          FINDING_WINDOW_RULES.find(({ prefixes }) =>
            prefixes.some((prefix) => condition.startsWith(prefix)),
          )?.preset,
      )
      .find((preset) => preset !== undefined) ?? "soft_tissue";
  const { center, width } = WINDOW_PRESETS.find((preset) => preset.id === id)!;
  return { center, width };
}
